import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Button, 
  Tooltip, 
  Menu, 
  MenuItem, 
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import {
  Description as DocumentIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Archive as ZipIcon,
  CheckCircle as ValidatedIcon
} from '@mui/icons-material';
import prdService from '../../services/prdService';
import useNotification from '../../hooks/useNotification';
import PermissionGate from '../common/PermissionGate';

/**
 * Component for PRD generation buttons
 * @param {Object} props - Component props
 * @param {string} props.projectId - The project ID
 * @returns {React.ReactElement} PRD generation buttons component
 */
const PRDGenerationButtons = ({ projectId }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingZip, setIsGeneratingZip] = useState(false);
  const [isGeneratingValidatedZip, setIsGeneratingValidatedZip] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [generatedDocumentId, setGeneratedDocumentId] = useState(null);
  const [prdType, setPrdType] = useState(null);
  const { showNotification } = useNotification();
  
  const open = Boolean(anchorEl);
  
  // Handle button click to open menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Handle customer PRD generation
  const handleCustomerPRD = async () => {
    handleClose();
    
    try {
      setIsGenerating(true);
      setPrdType('customer');
      
      // Show a notification to inform user that generation is in progress
      showNotification({
        message: 'Generating customer PRD... This may take a few minutes.',
        type: 'info'
      });
      
      const result = await prdService.generateCustomerPRD(projectId);
      
      console.log('Generated customer PRD:', result);
      setGeneratedDocumentId(result._id);
      setShowSuccessDialog(true);
      
      showNotification({
        message: 'Customer PRD generated successfully',
        type: 'success'
      });
      
      setIsGenerating(false);
    } catch (error) {
      handleGenerationError(error);
    }
  };
  
  // Handle developer PRD generation
  const handleDeveloperPRD = async () => {
    handleClose();
    
    try {
      setIsGenerating(true);
      setPrdType('developer');
      
      // Show a notification to inform user that generation is in progress
      showNotification({
        message: 'Generating developer PRD... This may take a few minutes.',
        type: 'info'
      });
      
      const result = await prdService.generateDeveloperPRD(projectId);
      
      console.log('Generated developer PRD:', result);
      setGeneratedDocumentId(result._id);
      setShowSuccessDialog(true);
      
      showNotification({
        message: 'Developer PRD generated successfully',
        type: 'success'
      });
      
      setIsGenerating(false);
    } catch (error) {
      handleGenerationError(error);
    }
  };
  
  // Handle view document click
  const handleViewDocument = () => {
    setShowSuccessDialog(false);
    
    if (generatedDocumentId) {
      window.open(`/documents/${generatedDocumentId}`, '_blank');
    }
  };
  
  // Handle ZIP generation
  const handleGenerateZip = async () => {
    try {
      setIsGeneratingZip(true);
      
      showNotification({
        message: 'Generating ZIP file with all task content...',
        type: 'info'
      });

      // Create API request to backend
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
      const response = await fetch(`${apiUrl}/documents/projects/${projectId}/generate-zip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to generate ZIP: ${response.statusText}`);
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'project_tasks.zip';
      if (contentDisposition && contentDisposition.includes('filename=')) {
        filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification({
        message: 'ZIP file downloaded successfully!',
        type: 'success'
      });

    } catch (error) {
      console.error('ZIP generation error:', error);
      showNotification({
        message: error.message || 'Failed to generate ZIP file. Please try again later.',
        type: 'error',
        duration: 5000
      });
    } finally {
      setIsGeneratingZip(false);
    }
  };

  // Handle Validated ZIP generation (only validated/approved items)
  const handleGenerateValidatedZip = async () => {
    try {
      setIsGeneratingValidatedZip(true);

      showNotification({
        message: 'Generating ZIP with validated content only...',
        type: 'info'
      });

      // Create API request to backend validated export endpoint
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
      const response = await fetch(`${apiUrl}/download/validated/${projectId}/zip`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to generate validated ZIP: ${response.statusText}`);
      }

      // Get the filename from the response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'validated_export.zip';
      if (contentDisposition && contentDisposition.includes('filename=')) {
        filename = contentDisposition.split('filename=')[1].replace(/"/g, '');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification({
        message: 'Validated ZIP file downloaded successfully!',
        type: 'success'
      });

    } catch (error) {
      console.error('Validated ZIP generation error:', error);
      showNotification({
        message: error.message || 'Failed to generate validated ZIP file. Please try again later.',
        type: 'error',
        duration: 5000
      });
    } finally {
      setIsGeneratingValidatedZip(false);
    }
  };

  // Handle when generation fails
  const handleGenerationError = (error) => {
    console.error('PRD generation error:', error);
    
    showNotification({
      message: error.message || 'Failed to generate PRD document. Please try again later.',
      type: 'error',
      duration: 5000
    });
    
    setIsGenerating(false);
  };
  
  return (
    <>
      <Box className="prd-generation-buttons" sx={{ display: 'flex !important', alignItems: 'center !important', gap: '12px !important' }}>
        <PermissionGate permission="foundation.generate.prd">
          <Tooltip title="Generate PRD document">
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={handleClick}
              startIcon={<DocumentIcon fontSize="small" />}
              endIcon={<ArrowDownIcon fontSize="small" />}
              disabled={isGenerating || isGeneratingZip || isGeneratingValidatedZip}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.5,
                boxShadow: 'none',
                height: '30px',
                '&:hover': {
                  boxShadow: 'none'
                }
              }}
            >
              {isGenerating ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Generating...
                </>
              ) : (
                'Generate PRD'
              )}
            </Button>
          </Tooltip>
        </PermissionGate>

        <PermissionGate permission="foundation.generate.zip">
          <Tooltip title="Download all task content as ZIP">
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleGenerateZip}
              startIcon={<ZipIcon fontSize="small" />}
              disabled={isGenerating || isGeneratingZip || isGeneratingValidatedZip}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.5,
                boxShadow: 'none',
                height: '30px',
                '&:hover': {
                  boxShadow: 'none'
                }
              }}
            >
              {isGeneratingZip ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Generating...
                </>
              ) : (
                'Generate ZIP'
              )}
            </Button>
          </Tooltip>
        </PermissionGate>

        <PermissionGate permission="foundation.generate.validatedzip">
          <Tooltip title="Download only validated/approved content as ZIP">
            <Button
              variant="outlined"
              size="small"
              onClick={handleGenerateValidatedZip}
              startIcon={<ValidatedIcon fontSize="small" />}
              disabled={isGenerating || isGeneratingZip || isGeneratingValidatedZip}
              sx={{
                borderRadius: '4px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.5,
                boxShadow: 'none',
                height: '30px',
                color: '#2e7d32',
                borderColor: '#2e7d32',
                '&:hover': {
                  boxShadow: 'none',
                  borderColor: '#1b5e20',
                  backgroundColor: 'rgba(46, 125, 50, 0.04)'
                }
              }}
            >
              {isGeneratingValidatedZip ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1, color: '#2e7d32' }} />
                  Generating...
                </>
              ) : (
                'Validated ZIP'
              )}
            </Button>
          </Tooltip>
        </PermissionGate>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleCustomerPRD}>
            Customer PRD
          </MenuItem>
          <MenuItem onClick={handleDeveloperPRD}>
            Developer PRD
          </MenuItem>
        </Menu>
      </Box>
      
      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        aria-labelledby="prd-success-dialog-title"
        aria-describedby="prd-success-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="prd-success-dialog-title" sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e9ecef', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentIcon color="primary" />
            PRD Generated Successfully
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <DialogContentText id="prd-success-dialog-description" sx={{ mb: 2 }}>
            {prdType === 'customer'
              ? 'The customer-facing PRD has been generated successfully. This document includes project information, features, user roles, user stories, process flows, and designs in a format suitable for customer validation and sign-off.'
              : 'The developer-facing PRD has been generated successfully. This document includes all technical details needed for implementation, including architecture, technical specifications, and development guidelines.'}
          </DialogContentText>
          
          <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1, mt: 1, border: '1px solid #e9ecef' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Included Sections:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              {prdType === 'customer' ? (
                <>
                  <li>Project Information</li>
                  <li>Features</li>
                  <li>User Roles</li>
                  <li>User Stories</li>
                  <li>Process Flows</li>
                  <li>Design System</li>
                  <li>Designs</li>
                </>
              ) : (
                <>
                  <li>Project Information</li>
                  <li>User Stories</li>
                  <li>Process Flows</li>
                  <li>Design System</li>
                  <li>Designs</li>
                  <li>Field Validations</li>
                  <li>Technology Stack</li>
                  <li>Architecture</li>
                  <li>Development Guidelines</li>
                  <li>Security Guidelines</li>
                  <li>User Permissions</li>
                  <li>Unit Testing</li>
                  <li>Functional Testing</li>
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e9ecef' }}>
          <Button onClick={() => setShowSuccessDialog(false)} variant="outlined">
            Close
          </Button>
          <Button 
            onClick={handleViewDocument} 
            variant="contained" 
            color="primary" 
            startIcon={<DocumentIcon />}
            autoFocus
          >
            View Document
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

PRDGenerationButtons.propTypes = {
  projectId: PropTypes.string.isRequired
};

export default PRDGenerationButtons;